import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Play, ShieldCheck } from "lucide-react";

const STATUS_TONE = {
  EXACT_MATCH: "bg-emerald-100 text-emerald-800 border-emerald-200",
  QUANTITY_MISMATCH: "bg-amber-100 text-amber-800 border-amber-200",
  RATE_MISMATCH: "bg-amber-100 text-amber-800 border-amber-200",
  AMOUNT_MISMATCH: "bg-amber-100 text-amber-800 border-amber-200",
  MISSING_IN_SYSTEM: "bg-rose-100 text-rose-800 border-rose-200",
  MISSING_IN_MANUAL: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function RecoveryShadowBilling() {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [manualCsv, setManualCsv] = useState(
    "order_code,service_code,quantity,rate,amount\nSTX-ORD-2026-000001,PACKING_MATERIAL,1,500,500\n");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [certifying, setCertifying] = useState(false);

  const runReconcile = useCallback(async () => {
    setBusy(true);
    try {
      const rows = manualCsv.trim().split("\n");
      const headers = rows[0].split(",").map((h) => h.trim());
      const entries = rows.slice(1).map((line) => {
        const cells = line.split(",").map((c) => c.trim());
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = ["quantity", "rate", "amount", "tax"].includes(h)
            ? Number(cells[i] || 0) : cells[i];
        });
        return obj;
      });
      const { data } = await api.post("/recovery/shadow-billing/reconcile", {
        billing_period: period,
        manual_entries: entries,
      });
      setResults(data);
      toast.success(`Reconciled — ${data.total} row(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Reconcile failed"); }
    finally { setBusy(false); }
  }, [period, manualCsv]);

  const bulkCertify = async () => {
    setCertifying(true);
    try {
      const { data } = await api.post(
        `/recovery/shadow-billing/bulk-certify?billing_period=${period}`);
      toast.success(`Certified ${data.certified} exact-match event(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Certify failed"); }
    finally { setCertifying(false); }
  };

  const exactCount = (results?.counts || {}).EXACT_MATCH || 0;

  return (
    <div className="max-w-5xl space-y-4" data-testid="shadow-billing-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Commercial · Monthly Billing</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 mt-1">Recovery Shadow Billing</h1>
        <p className="text-sm text-slate-500 mt-1">Compare Suntronix ONE billable events against Finance's manual entries. Only EXACT matches can be bulk certified.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="flex gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Billing period</div>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="h-9 w-40" data-testid="sb-period" />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Manual entries (CSV — headers: order_code, service_code, quantity, rate, amount)
          </div>
          <Textarea rows={5} value={manualCsv} onChange={(e) => setManualCsv(e.target.value)}
            className="font-mono text-[12px]" data-testid="sb-manual-csv" />
        </div>
        <div className="flex gap-2">
          <Button onClick={runReconcile} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="sb-run">
            <Play className="h-4 w-4 mr-1.5" />
            {busy ? "Running…" : "Run reconciliation"}
          </Button>
          {exactCount > 0 && (
            <Button onClick={bulkCertify} disabled={certifying}
              variant="outline" data-testid="sb-bulk-certify"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <ShieldCheck className="h-4 w-4 mr-1.5" />
              Bulk certify {exactCount} exact match(es)
            </Button>
          )}
        </div>
      </div>

      {results && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex gap-3 items-center mb-3">
            <div className="text-[13px] font-semibold text-slate-900">Reconciliation results</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(results.counts || {}).map(([k, v]) => (
                <span key={k}
                  className={"text-[11px] px-2 py-0.5 rounded border " + (STATUS_TONE[k] || "border-slate-200 bg-slate-50")}>
                  {k.replace(/_/g, " ")}: <span className="font-semibold">{v}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="divide-y">
            {(results.results || []).slice(0, 100).map((r, i) => (
              <div key={i} className="py-2 flex items-center gap-3 text-[12px] font-mono"
                data-testid={`sb-row-${i}`}>
                <span className="min-w-[180px]">{r.order_code}</span>
                <span className="flex-1 min-w-[140px]">{r.service_code}</span>
                <span className="text-slate-500 min-w-[100px]">
                  sys: {r.system?.amount ?? "—"}  /  manual: {r.manual?.amount ?? "—"}
                </span>
                <span className={"text-[10px] px-1.5 py-0.5 rounded border " + (STATUS_TONE[r.status] || "")}>
                  {r.status.replace(/_/g, " ")}
                </span>
                {r.status === "EXACT_MATCH" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
